import img from "../assests/images/header-logo.png";
import img2 from "../assests/images/South-Travels-Footer-Logo.png";

import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  Percent,
  Plane,
  Hotel,
  PhoneCall,
  Mail,
  Menu,
  X,
  Heart,
  User,
  LogOut,
} from "lucide-react";

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // SCROLL DETECTION
  // =====================================================

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // =====================================================
  // CLOSE USER DROPDOWN WHEN CLICKING OUTSIDE
  // =====================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =====================================================
  // CLOSE MOBILE MENU WHEN ROUTE CHANGES
  // =====================================================

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  }, [location.pathname]);

  // =====================================================
  // PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  // =====================================================

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // =====================================================
  // SCROLL TO TOP
  // =====================================================

  const navigateTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // CLOSE MOBILE MENU
  // =====================================================

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  // =====================================================
  // LOGOUT
  // =====================================================

  const logout = () => {
    localStorage.removeItem("user");

    setUser(null);

    setDropdownOpen(false);
    setMobileUserOpen(false);
    setMobileMenuOpen(false);

    navigate("/");
  };

  // =====================================================
  // DESKTOP NAVIGATION STYLE
  // =====================================================

  const desktopNavClass = ({ isActive }) => `
    relative
    font-medium
    text-sm
    py-2
    transition-all
    duration-300

    ${isActive
      ? "text-[#D4A83F]"
      : isScrolled
        ? "text-[#0F172A] hover:text-[#D4A83F]"
        : "text-white hover:text-[#D4A83F]"
    }
  `;

  // =====================================================
  // MOBILE NAVIGATION STYLE
  // =====================================================

  const mobileNavClass = ({ isActive }) => `
    block
    w-full
    py-4
    border-b
    border-gray-100
    font-medium
    transition-all
    duration-300

    ${isActive
      ? "text-[#D4A83F]"
      : "text-gray-700 hover:text-[#D4A83F]"
    }
  `;

  return (
    <>
      {/* =====================================================
          TOP BAR
      ===================================================== */}

      <div className="bg-[#D4A83F] text-white text-xs overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex">
          <div className="flex whitespace-nowrap animate-[marquee_20s_linear_infinite] gap-12">

            <span className="flex items-center gap-2 font-medium">
              <Percent size={15} />

              Summer Sale:

              <span className="font-semibold">
                Save up to 60%
              </span>

              on Dubai Hotels
            </span>

            <span className="flex items-center gap-2">
              <Plane size={15} />

              Best Flight Deals Available Today
            </span>

            <span className="flex items-center gap-2">
              <Hotel size={15} />

              Free Cancellation on Selected Hotels
            </span>

            <a
              href="tel:+971501234567"
              className="flex items-center gap-2 hover:text-white/80 transition"
            >
              <PhoneCall size={15} />

              +971 50 123 4567
            </a>

            <a
              href="mailto:info@southtravels.com"
              className="flex items-center gap-2 hover:text-white/80 transition"
            >
              <Mail size={15} />

              info@southtravels.com
            </a>

          </div>
        </div>
      </div>

      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <header
        className={`
          sticky
          top-0
          z-50
          w-full
          py-1
          transition-all
          duration-500
          ${isScrolled
            ? "bg-white shadow-md"
            : "bg-[#002B45] shadow-lg"
          }
        `}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">

          <div
            className={`
              flex
              items-center
              justify-between
              transition-all
              duration-300

              ${isScrolled
                ? "h-[60px]"
                : "h-[72px] sm:h-[80px]"
              }
            `}
          >

            {/* =================================================
                LOGO
            ================================================= */}

            <NavLink
              to="/"
              onClick={navigateTop}
              className="relative flex items-center shrink-0"
            >

              {/* NAVY HEADER LOGO */}

              <img
                src={img2}
                alt="Beach Hotels Dubai"
                className={`
                  w-auto
                  object-contain
                  transition-all
                  duration-500

                  ${isScrolled
                    ? "opacity-0 scale-95 h-10 sm:h-12"
                    : "opacity-100 scale-100 h-12 sm:h-16"
                  }
                `}
              />

              {/* SCROLLED HEADER LOGO */}

              <img
                src={img}
                alt="Beach Hotels Dubai"
                className={`
                  absolute
                  left-0
                  top-1/2
                  -translate-y-1/2
                  w-auto
                  object-contain
                  transition-all
                  duration-500

                  ${isScrolled
                    ? "opacity-100 scale-100 h-10 sm:h-12"
                    : "opacity-0 scale-95 h-12 sm:h-16"
                  }
                `}
              />

            </NavLink>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav className="hidden lg:flex items-center gap-7 xl:gap-8">

              <NavLink
                to="/"
                className={desktopNavClass}
              >
                Home
              </NavLink>

              <NavLink
                to="/hotels"
                className={desktopNavClass}
              >
                Hotels
              </NavLink>

              <NavLink
                to="/about-us"
                className={desktopNavClass}
              >
                About Us
              </NavLink>

              <NavLink
                to="/services"
                className={desktopNavClass}
              >
                Services
              </NavLink>

              <NavLink
                to="/blogs"
                className={desktopNavClass}
              >
                Blog
              </NavLink>

              <NavLink
                to="/contact-us"
                className={desktopNavClass}
              >
                Contact
              </NavLink>

            </nav>

            {/* =================================================
                RIGHT SIDE
            ================================================= */}

            <div className="hidden lg:flex items-center gap-4">

              {/* WISHLIST */}

              <NavLink
                to="/wishlist"
                className={`
                  flex
                  items-center
                  gap-2
                  px-3
                  py-2
                  rounded-full
                  text-sm
                  transition-all
                  duration-300

                  ${isScrolled
                    ? "bg-[#F8F2E5] text-[#002B45] hover:bg-[#D4A83F] hover:text-white"
                    : "bg-white/10 text-white hover:bg-[#D4A83F]"
                  }
                `}
              >
                <Heart
                  size={16}
                  strokeWidth={2}
                />

                Wishlist
              </NavLink>

              {/* =================================================
                  LOGIN / USER
              ================================================= */}

              {!user ? (

                <button
                  onClick={onLoginClick}
                  className="
                    px-5
                    py-2
                    bg-gradient-to-r
                    from-[#002B45]
                    via-[#174B5F]
                    to-[#D4A83F]
                    hover:from-[#D4A83F]
                    hover:via-[#D4A83F]
                    hover:to-[#002B45]
                    text-white
                    rounded-xl
                    hover:shadow-lg
                    hover:scale-105
                    transition-all
                    duration-300
                  "
                >
                  Login
                </button>

              ) : (

                <div
                  ref={dropdownRef}
                  className="relative"
                >

                  <button
                    type="button"
                    onClick={() =>
                      setDropdownOpen(!dropdownOpen)
                    }
                    className={`
                      flex
                      items-center
                      gap-3
                      rounded-xl
                      px-4
                      py-2
                      transition-all
                      duration-300

                      ${isScrolled
                        ? "border border-[#D4A83F]/30 text-[#0F172A] hover:border-[#D4A83F] hover:shadow-md"
                        : "border border-white/30 text-white hover:border-[#D4A83F] hover:shadow-md"
                      }
                    `}
                  >

                    <div
                      className="
                        w-8
                        h-8
                        rounded-full
                        bg-gradient-to-r
                        from-[#002B45]
                        via-[#174B5F]
                        to-[#D4A83F]
                        flex
                        items-center
                        justify-center
                        text-white
                        font-semibold
                        shadow-md
                      "
                    >
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>

                    <span>
                      {user?.name?.split(" ")[0]}
                    </span>

                  </button>

                  {/* USER DROPDOWN */}

                  {dropdownOpen && (

                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        mt-2
                        w-52
                        bg-white
                        shadow-2xl
                        border
                        border-[#D4A83F]/20
                        rounded-2xl
                        overflow-hidden
                        z-[100]
                      "
                    >

                      <NavLink
                        to="/profile"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-gray-700
                          hover:bg-[#F8F2E5]
                          hover:text-[#D4A83F]
                          transition
                        "
                      >
                        <User size={17} />

                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-gray-700
                          hover:bg-[#F8F2E5]
                          hover:text-[#D4A83F]
                          transition
                        "
                      >
                        <Heart size={17} />

                        Wishlist
                      </NavLink>

                      <button
                        type="button"
                        onClick={logout}
                        className="
                          w-full
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-red-500
                          hover:bg-red-50
                          transition
                        "
                      >
                        <LogOut size={17} />

                        Logout
                      </button>

                    </div>

                  )}

                </div>

              )}

            </div>

            {/* =================================================
                MOBILE BUTTON
            ================================================= */}

            <div className="lg:hidden">

              <button
                type="button"
                onClick={() =>
                  setMobileMenuOpen(true)
                }
                aria-label="Open mobile menu"
                className={`
                  w-11
                  h-11
                  flex
                  items-center
                  justify-center
                  rounded-xl
                  transition-all
                  duration-300

                  ${isScrolled
                    ? "border border-[#D4A83F]/40 bg-white text-[#002B45] hover:bg-[#D4A83F] hover:text-white"
                    : "border border-white/30 bg-white/10 text-white hover:bg-[#D4A83F]"
                  }
                `}
              >
                <Menu size={25} />
              </button>

            </div>

          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
          
          IMPORTANT:
          This is OUTSIDE the header.
          This prevents backdrop-blur / header stacking issues.
      ===================================================== */}

      <div
        onClick={closeMobileMenu}
        className={`
          fixed
          inset-0
          bg-black/50
          backdrop-blur-sm
          z-[9998]
          transition-opacity
          duration-300
          lg:hidden

          ${mobileMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
          }
        `}
      />

      {/* =====================================================
          MOBILE SIDEBAR
          
          IMPORTANT:
          This is also OUTSIDE the header.
      ===================================================== */}

      <aside
        className={`
          fixed
          top-0
          left-0
          h-screen
          w-full
          max-w-[380px]
          bg-white
          shadow-2xl
          z-[9999]
          overflow-y-auto
          transition-transform
          duration-300
          ease-in-out
          lg:hidden

          ${mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full"
          }
        `}
      >

        {/* =================================================
            MOBILE SIDEBAR HEADER
        ================================================= */}

        <div
          className="
            bg-gradient-to-r
            from-[#002B45]
            via-[#174B5F]
            to-[#D4A83F]
            px-5
            py-5
          "
        >

          <div className="flex items-center justify-between">

            {/* MOBILE LOGO */}

            <NavLink
              to="/"
              onClick={() => {
                closeMobileMenu();
                navigateTop();
              }}
              className="flex items-center"
            >
              <img
                src={img2}
                alt="Beach Hotels Dubai"
                className="
                  h-12
                  w-auto
                  object-contain
                "
              />
            </NavLink>

            {/* CLOSE */}

            <button
              type="button"
              onClick={closeMobileMenu}
              aria-label="Close mobile menu"
              className="
                w-10
                h-10
                rounded-full
                bg-white/20
                text-white
                flex
                items-center
                justify-center
                hover:bg-white/30
                transition
              "
            >
              <X size={23} />
            </button>

          </div>

        </div>

        {/* =================================================
            MOBILE USER SECTION
        ================================================= */}

        <div className="px-5 py-4 border-b border-gray-100">

          {user ? (

            <div
              ref={mobileUserRef}
              className="relative"
            >

              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen(!mobileUserOpen)
                }
                className="
                  flex
                  items-center
                  gap-4
                  w-full
                "
              >

                {/* AVATAR */}

                <div
                  className="
                    w-12
                    h-12
                    rounded-full
                    bg-gradient-to-r
                    from-[#002B45]
                    via-[#174B5F]
                    to-[#D4A83F]
                    flex
                    items-center
                    justify-center
                    text-white
                    font-bold
                    text-lg
                    shadow-md
                    shrink-0
                  "
                >
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>

                {/* USER INFO */}

                <div className="text-left flex-1">

                  <p className="text-base font-semibold text-gray-800">
                    {user?.name}
                  </p>

                  <p className="text-xs text-gray-500">
                    Tap to manage account
                  </p>

                </div>

                {/* ARROW */}

                <span
                  className={`
                    text-gray-500
                    transition-transform
                    duration-300

                    ${mobileUserOpen
                      ? "rotate-180"
                      : ""
                    }
                  `}
                >
                  ▼
                </span>

              </button>

              {/* USER MENU */}

              {mobileUserOpen && (

                <div
                  className="
                    mt-3
                    bg-gray-50
                    border
                    border-gray-100
                    rounded-xl
                    overflow-hidden
                  "
                >

                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-gray-700
                      hover:bg-[#F8F2E5]
                      hover:text-[#D4A83F]
                      transition
                    "
                  >
                    <User size={17} />

                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-gray-700
                      hover:bg-[#F8F2E5]
                      hover:text-[#D4A83F]
                      transition
                    "
                  >
                    <Heart size={17} />

                    Wishlist
                  </NavLink>

                  <button
                    type="button"
                    onClick={logout}
                    className="
                      w-full
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-red-500
                      hover:bg-red-50
                      transition
                    "
                  >
                    <LogOut size={17} />

                    Logout
                  </button>

                </div>

              )}

            </div>

          ) : (

            /* LOGIN BUTTON */

            <button
              type="button"
              onClick={() => {
                onLoginClick();
                closeMobileMenu();
              }}
              className="
                w-full
                py-3.5
                rounded-xl
                bg-gradient-to-r
                from-[#002B45]
                via-[#174B5F]
                to-[#D4A83F]
                hover:from-[#D4A83F]
                hover:via-[#D4A83F]
                hover:to-[#002B45]
                text-white
                font-semibold
                shadow-md
                hover:shadow-xl
                transition-all
                duration-300
              "
            >
              Login / Sign Up
            </button>

          )}

        </div>

        {/* =================================================
            MOBILE NAVIGATION
        ================================================= */}

        <nav className="px-5 py-4">

          <NavLink
            to="/"
            onClick={() => {
              closeMobileMenu();
              navigateTop();
            }}
            className={mobileNavClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/hotels"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Hotels
          </NavLink>

          <NavLink
            to="/about-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            About Us
          </NavLink>

          <NavLink
            to="/services"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Services
          </NavLink>

          <NavLink
            to="/blogs"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Blog
          </NavLink>

          <NavLink
            to="/contact-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Contact
          </NavLink>

          {/* WISHLIST */}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="
              mt-5
              flex
              items-center
              justify-center
              gap-2
              py-3
              rounded-xl
              bg-[#F8F2E5]
              text-[#002B45]
              font-semibold
              hover:bg-[#D4A83F]
              hover:text-white
              transition
            "
          >
            <Heart size={19} />

            Wishlist
          </NavLink>

        </nav>

      </aside>
    </>
  );
}